import { useTranslations } from 'next-intl'

import { ReceiptStatus, TaxReceiptType } from '@/entities/user'
import {
  formatTaxInvoiceNumber,
  ReceiptInformationUpdate,
  useGuestReceiptInformation,
  useReceiptInformationValidation,
  useTaxReceiptTypeMap,
} from '@/features/update-receipt-information'
import { formatPhoneNumber } from '@/shared/lib'
import { Button } from '@/shared/ui'

import { useGetReceiptBillUrl } from '../../api/get-receipt-bill-url'

type Props = {
  contractId: number
}

export const ReceiptInformation = ({ contractId }: Props) => {
  const t = useTranslations('receipt_information')

  const { TAX_RECEIPT_TYPE_MAP } = useTaxReceiptTypeMap()

  const { receiptInformation, hasReceiptInformation } =
    useGuestReceiptInformation({ contractId })

  const { handleReceiptFormOpen } = useReceiptInformationValidation()

  const { getReceiptBillUrl, isLoading: isReceiptBillUrlLoading } =
    useGetReceiptBillUrl()

  const receiptRequested = receiptInformation?.receiptRequested
  const isFailed = receiptInformation?.status === ReceiptStatus.FAILED
  const isCompleted = receiptInformation?.status === ReceiptStatus.COMPLETED

  const formatIssuanceNumber = (issuanceNumber: string) => {
    if (receiptInformation?.taxReceiptType === TaxReceiptType.TAX_INVOICE) {
      return formatTaxInvoiceNumber(issuanceNumber)
    }

    return formatPhoneNumber(issuanceNumber)
  }

  const handleReceiptButtonClick = async () => {
    const { response, error } = await getReceiptBillUrl(contractId)

    if (error) {
      return
    }

    if (response?.billUrl) {
      window.open(response?.billUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div className="space-y-3">
      <h3 className="typo-body1 font-semibold text-black">{t('title')}</h3>

      {(() => {
        if (isFailed) {
          return (
            <div className="space-y-2">
              <p className="typo-label1 font-medium text-gray-900">
                {t('status.issue_failed.label')}
              </p>
              <p className="typo-label2 mb-3 font-normal whitespace-break-spaces text-gray-500">
                {receiptInformation.failedReason}
              </p>
            </div>
          )
        }

        if (isCompleted && receiptRequested) {
          const taxReceiptType =
            TAX_RECEIPT_TYPE_MAP[
              receiptInformation.taxReceiptType as TaxReceiptType
            ]

          return (
            <div className="space-y-4">
              <p className="typo-label1 font-medium text-gray-900">
                {t('status.requested', {
                  type: taxReceiptType,
                  number: formatIssuanceNumber(
                    receiptInformation.issuanceNumber!,
                  ),
                })}
              </p>
              {receiptInformation.billUrl && (
                <Button
                  variant="outline"
                  size="medium"
                  shape="pill"
                  fullWidth
                  onClick={handleReceiptButtonClick}
                  disabled={isReceiptBillUrlLoading}
                >
                  {taxReceiptType}
                </Button>
              )}
            </div>
          )
        }

        if (isCompleted && !receiptRequested) {
          return (
            <div>
              <p className="typo-label1 mb-2 font-medium text-gray-900">
                {t('status.not_requested.label')}
              </p>
              <p className="typo-label2 mb-4 font-normal whitespace-break-spaces text-gray-500">
                {t('status.not_requested.description')}
              </p>

              <Button
                variant="outline"
                size="medium"
                shape="pill"
                fullWidth
                onClick={handleReceiptButtonClick}
                disabled={isReceiptBillUrlLoading}
              >
                {t('button.self_issue')}
              </Button>
            </div>
          )
        }

        return (
          <ReceiptInformationUpdate
            defaultFormOpen={!hasReceiptInformation(receiptInformation)}
            hasDefaultReceiptInformation={hasReceiptInformation(
              receiptInformation,
            )}
            onFormOpen={handleReceiptFormOpen}
          />
        )
      })()}
    </div>
  )
}
